import { useEffect, useState } from 'react';
import { Me, Public } from '../api.js';
import { FormField, Switch } from '../components/FormField.jsx';
import { DynamicField, validateCustomFieldsClient } from '../components/DynamicField.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { useAuth } from '../AuthContext.jsx';
import { auth, sendEmailVerification, authActionSettings } from '../firebase.js';

export default function Profile() {
  const { emailVerified, refreshVerification } = useAuth();
  const [user, setUser] = useState(null);
  const [customDefs, setCustomDefs] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [verifyState, setVerifyState] = useState('idle');
  const [verifyErr, setVerifyErr] = useState(null);

  useEffect(() => {
    Me.get().then((r) => {
      setUser(r.user);
      setCustomFields(r.user.customFields || {});
    });
    Public.signupFields().then((r) => setCustomDefs(r.fields)).catch(() => setCustomDefs([]));
  }, []);

  function updateCustom(key, v) {
    setCustomFields((c) => ({ ...c, [key]: v }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function resendVerification() {
    if (!auth.currentUser) return;
    setVerifyState('sending');
    setVerifyErr(null);
    try {
      await sendEmailVerification(auth.currentUser, authActionSettings);
      setVerifyState('sent');
    } catch (e) {
      setVerifyState('error');
      setVerifyErr(e.message);
    }
  }

  async function checkVerification() {
    const ok = await refreshVerification();
    if (!ok) setMsg('Still not verified — check your inbox and click the link.');
    else setMsg('Verified!');
  }

  async function save(e) {
    e.preventDefault();
    setMsg(null);
    setFieldErrors({});

    const pre = validateCustomFieldsClient(customDefs, customFields);
    if (!pre.ok) {
      setFieldErrors(pre.errorsByKey);
      setMsg('Please fix the highlighted fields.');
      return;
    }

    setSaving(true);
    try {
      const r = await Me.update({
        displayName: user.displayName,
        phone: user.phone,
        marketingOptIn: user.marketingOptIn,
        customFields,
      });
      setUser(r.user);
      setCustomFields(r.user.customFields || {});
      setMsg('Saved.');
    } catch (e) {
      if (e.data?.issues && Array.isArray(e.data.issues)) {
        const byKey = {};
        for (const it of e.data.issues) if (it.key) byKey[it.key] = it.message;
        setFieldErrors(byKey);
        setMsg('Please fix the highlighted fields.');
      } else {
        setMsg(e.message);
      }
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <div className="p-8 text-ink-3">Loading…</div>;

  return (
    <div className="max-w-xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <PageHeader eyebrow="Account" title="Your profile" />
      {!emailVerified && (
        <div className="card mb-6 bg-warn/5 border-warn/30">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="eyebrow text-warn mb-1">Email not verified</div>
              <p className="text-sm text-ink">
                We sent a verification link to <strong>{user.email}</strong>. Click it to confirm your
                address. Already clicked? Hit <em>I've verified</em> to refresh.
              </p>
              {verifyState === 'sent' && (
                <p className="text-xs text-success mt-2">Verification email sent. Check your inbox.</p>
              )}
              {verifyErr && <p className="text-xs text-danger mt-2">{verifyErr}</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={resendVerification}
              className="btn-outline btn-sm"
              disabled={verifyState === 'sending'}
            >
              {verifyState === 'sending' ? 'Sending…' : 'Resend verification'}
            </button>
            <button type="button" onClick={checkVerification} className="btn-ghost btn-sm">
              I've verified — refresh
            </button>
          </div>
        </div>
      )}
      <form onSubmit={save} className="card space-y-5">
        <FormField label="Email">
          <input className="input bg-line-soft/50 text-ink-2" value={user.email} disabled />
        </FormField>
        <FormField label="Name">
          <input
            className="input"
            value={user.displayName || ''}
            onChange={(e) => setUser({ ...user, displayName: e.target.value })}
          />
        </FormField>
        <FormField label="Phone">
          <input
            className="input"
            value={user.phone || ''}
            onChange={(e) => setUser({ ...user, phone: e.target.value })}
          />
        </FormField>

        {customDefs.length > 0 && (
          <div className="pt-2 border-t border-line space-y-5">
            <div className="eyebrow">About you</div>
            {customDefs.map((f) => (
              <DynamicField
                key={f.id}
                field={f}
                value={customFields[f.key]}
                onChange={(v) => updateCustom(f.key, v)}
                error={fieldErrors[f.key]}
              />
            ))}
          </div>
        )}

        <div className="pt-2 border-t border-line">
          <Switch
            checked={!!user.marketingOptIn}
            onChange={(v) => setUser({ ...user, marketingOptIn: v })}
            label="Receive marketing emails"
            hint="Club news, fixtures, and fundraisers"
          />
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {msg && <span className="text-xs text-ink-2">{msg}</span>}
        </div>
      </form>
    </div>
  );
}
